import { useCallback, useEffect, useState } from "react";

/**
 * A custom hook that manages form state in a consistent way
 * @param {Object} initialData - Initial form data
 * @param {Function} onSubmit - Submit callback function
 * @param {Function} validateForm - Optional validation function
 * @returns {Object} Form handlers and state
 */
export const useFormState = (initialData = {}, onSubmit, validateForm) => {
  const [formData, setFormData] = useState(initialData);

  const [changedFields, setChangedFields] = useState({});

  const [errors, setErrors] = useState({});

  useEffect(() => {
    setFormData(prevData => {
      const newData = { ...initialData };

      Object.entries(changedFields).forEach(([key, value]) => {
        if (value && typeof value === "object" && !Array.isArray(value)) {
          newData[key] = {
            ...(initialData[key] || {}),
            ...value,
          };
        } else {
          newData[key] = value;
        }
      });

      return newData;
    });
  }, [initialData, changedFields]);

  const handleInputChange = useCallback(
    e => {
      if (!e || !e.target) return;
      
      const { name, value } = e.target;
      
      if (!name) return;

      if (name.includes(".")) {
        const [parent, child] = name.split(".");

        setFormData(prevData => ({
          ...prevData,
          [parent]: {
            ...(prevData[parent] || {}),
            [child]: value,
          },
        }));

        setChangedFields(prevChanges => ({
          ...prevChanges,
          [parent]: {
            ...(prevChanges[parent] || {}),
            [child]: value,
          },
        }));
      } else {
        setFormData(prevData => ({
          ...prevData,
          [name]: value,
        }));

        setChangedFields(prevChanges => ({
          ...prevChanges,
          [name]: value,
        }));
      }

      if (errors[name]) {
        setErrors(prevErrors => {
          const newErrors = { ...prevErrors };
          delete newErrors[name];
          return newErrors;
        });
      }
    },
    [errors]
  );

  const handleDateChange = useCallback(
    (name, timestamp) => {
      setFormData(prevData => ({
        ...prevData,
        [name]: timestamp,
      }));

      setChangedFields(prevChanges => ({
        ...prevChanges,
        [name]: timestamp,
      }));

      if (errors[name]) {
        setErrors(prevErrors => {
          const newErrors = { ...prevErrors };
          delete newErrors[name];
          return newErrors;
        });
      }
    },
    [errors]
  );

  const handleSubmit = useCallback(
    async e => {
      if (e) e.preventDefault();

      if (typeof validateForm === "function") {
        const validationErrors = validateForm(formData);
        if (Object.keys(validationErrors).length > 0) {
          setErrors(validationErrors);
          return false;
        }
      }

      if (typeof onSubmit === "function") {
        await onSubmit(formData, changedFields);

        return true;
      }

      return false;
    },
    [formData, changedFields, validateForm, onSubmit]
  );

  const resetForm = useCallback(() => {
    setFormData(initialData);
    setChangedFields({});
    setErrors({});
  }, [initialData]);

  return {
    formData,
    setFormData,
    changedFields,
    errors,
    setErrors,
    handleInputChange,
    handleDateChange,
    handleSubmit,
    resetForm,
  };
};
